<template>
    <div>
        <draggable
            v-model="props.data.items"
            handle=".custom-block-handle"
            item-key="unique_id"
            :animation="150"
            class="space-y-4"
        >
            <template #item="{ element: item }">
                <div class="rounded border bg-white">
                    <div class="flex justify-between p-4 custom-block-handle">
                        <div class="flex items-center space-x-3 truncate">
                            <button type="button" class="cursor-move focus:outline-none">
                                <svg class="w-5 h-5 text-gray-500 hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path></svg>
                            </button>
                            <span class="text-sm truncate cursor-pointer" @click="toggleItem(item)">
                                {{ getItemLabel(item) }}
                            </span>
                        </div>
                        <div class="flex space-x-2">
                            <button type="button" class="focus:outline-none" @click="removeItem(item)">
                                <svg class="w-5 h-5 text-gray-500 hover:text-red-600" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" d="M0 0h24v24H0z"/><path d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"/></svg>
                            </button>
                            <button type="button" class="focus:outline-none" @click="toggleItem(item)">
                                <svg class="w-5 h-5 text-gray-500 hover:text-gray-900" :class="{ 'text-teal-600 hover:text-teal-700': item.edit }" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" d="M0 0h24v24H0z"/><path d="M3.34 17a10.018 10.018 0 0 1-.978-2.326 3 3 0 0 0 .002-5.347A9.99 9.99 0 0 1 4.865 4.99a3 3 0 0 0 4.631-2.674 9.99 9.99 0 0 1 5.007.002 3 3 0 0 0 4.632 2.672c.579.59 1.093 1.261 1.525 2.01.433.749.757 1.53.978 2.326a3 3 0 0 0-.002 5.347 9.99 9.99 0 0 1-2.501 4.337 3 3 0 0 0-4.631 2.674 9.99 9.99 0 0 1-5.007-.002 3 3 0 0 0-4.632-2.672A10.018 10.018 0 0 1 3.34 17zm5.66.196a4.993 4.993 0 0 1 2.25 2.77c.499.047 1 .048 1.499.001A4.993 4.993 0 0 1 15 17.197a4.993 4.993 0 0 1 3.525-.565c.29-.408.54-.843.748-1.298A4.993 4.993 0 0 1 18 12c0-1.26.47-2.437 1.273-3.334a8.126 8.126 0 0 0-.75-1.298A4.993 4.993 0 0 1 15 6.804a4.993 4.993 0 0 1-2.25-2.77c-.499-.047-1-.048-1.499-.001A4.993 4.993 0 0 1 9 6.803a4.993 4.993 0 0 1-3.525.565 7.99 7.99 0 0 0-.748 1.298A4.993 4.993 0 0 1 6 12c0 1.26-.47 2.437-1.273 3.334a8.126 8.126 0 0 0 .75 1.298A4.993 4.993 0 0 1 9 17.196zM12 15a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0-2a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/></svg>
                            </button>
                        </div>
                    </div>

                    <div v-if="item.edit" class="p-4 bg-gray-50 bg-opacity-75 border-t space-y-4">
                        <!-- Text item -->
                        <div v-if="item.type === 'text'">
                            <text-editor
                                :text="item.content"
                                @input="(newValue) => { item.content = newValue }"
                            />
                        </div>

                        <!-- Image item -->
                        <div v-else-if="item.type === 'image'">
                            <div class="flex items-center justify-between mb-1.5">
                                <label class="text-sm">Image</label>
                                <div v-if="item.files?.content_block_image?.url" class="flex items-center">
                                    <span class="text-xs text-gray-400 mr-1">Border:</span>
                                    <select
                                        :value="(item.image_border ?? true) ? 'show' : 'hide'"
                                        @change="item.image_border = $event.target.value === 'show'"
                                        class="border-0 focus:ring-0 leading-4 text-xs text-gray-500 p-0 pr-6 bg-transparent cursor-pointer"
                                    >
                                        <option value="show">Show</option>
                                        <option value="hide">Hide</option>
                                    </select>
                                </div>
                            </div>
                            <ResourceFileUpload
                                :unique_id="item.unique_id"
                                :data="item.files.content_block_image"
                                accept="image/*"
                                title="Click to upload or drag & drop"
                                name="content_block_image"
                                size="dropzone"
                                no_src="/images/upload-image.png"
                                @uploaded="(data) => item.files.content_block_image = data"
                                @deleted="(data) => item.files.content_block_image = data"
                            />
                        </div>

                        <!-- Content block item -->
                        <div v-else-if="item.type === 'contentBlock'" class="grid lg:grid-cols-2 gap-6 gap-y-4">
                            <div class="col-span-2">
                                <div class="flex items-center justify-between mb-1.5">
                                    <label class="text-sm">Image</label>
                                    <div v-if="item.files?.content_block_image?.url" class="flex items-center gap-3">
                                        <div class="flex items-center">
                                            <span class="text-xs text-gray-400 mr-1">Position:</span>
                                            <select
                                                :value="item.image_position || 'top'"
                                                @change="item.image_position = $event.target.value"
                                                class="border-0 focus:ring-0 leading-4 text-xs text-gray-500 p-0 pr-6 bg-transparent cursor-pointer"
                                            >
                                                <option value="top">Top</option>
                                                <option value="left">Left</option>
                                                <option value="right">Right</option>
                                            </select>
                                        </div>
                                        <div v-if="item.image_position === 'left' || item.image_position === 'right'" class="flex items-center">
                                            <span class="text-xs text-gray-400 mr-1">Width:</span>
                                            <select
                                                :value="item.image_width || '1/2'"
                                                @change="item.image_width = $event.target.value"
                                                class="border-0 focus:ring-0 leading-4 text-xs text-gray-500 p-0 pr-6 bg-transparent cursor-pointer"
                                            >
                                                <option value="1/2">1/2</option>
                                                <option value="2/5">2/5</option>
                                                <option value="1/3">1/3</option>
                                            </select>
                                        </div>
                                        <div class="flex items-center">
                                            <span class="text-xs text-gray-400 mr-1">Border:</span>
                                            <select
                                                :value="(item.image_border ?? true) ? 'show' : 'hide'"
                                                @change="item.image_border = $event.target.value === 'show'"
                                                class="border-0 focus:ring-0 leading-4 text-xs text-gray-500 p-0 pr-6 bg-transparent cursor-pointer"
                                            >
                                                <option value="show">Show</option>
                                                <option value="hide">Hide</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <ResourceFileUpload
                                    :unique_id="item.unique_id"
                                    :data="item.files.content_block_image"
                                    accept="image/*"
                                    title="Click to upload or drag & drop"
                                    name="content_block_image"
                                    size="dropzone"
                                    no_src="/images/upload-image.png"
                                    @uploaded="(data) => item.files.content_block_image = data"
                                    @deleted="(data) => item.files.content_block_image = data"
                                />
                            </div>
                            <div class="col-span-2">
                                <text-field
                                    label="Title"
                                    v-model="item.title"
                                    placeholder="Enter a title or leave it empty"
                                />
                            </div>
                            <div class="col-span-2">
                                <text-editor
                                    :text="item.content"
                                    @input="(newValue) => { item.content = newValue }"
                                />
                            </div>
                            <text-field
                                label="Button"
                                v-model="item.button"
                                placeholder="Enter button text"
                            />
                            <text-field
                                label="URL"
                                v-model="item.url"
                                placeholder="https://"
                                :prefixes="['https://', 'http://']"
                            />
                        </div>

                        <!-- Accordion item -->
                        <div v-else-if="item.type === 'accordion'">
                            <draggable
                                v-model="item.items"
                                handle=".handle"
                                item-key="unique_id"
                                :animation="150"
                            >
                                <template #item="{ element: accItem }">
                                    <draggable-accordion
                                        class="mb-2"
                                        :unique_id="accItem.unique_id"
                                        :edit="accItem.edit"
                                        @remove="removeAccordionItem(item, $event)"
                                        @toggle="toggleAccordionItem(item, accItem)"
                                    >
                                        <template #title>{{ accItem.question || 'Item' }}</template>
                                        <template #content>
                                            <div class="grid lg:grid-cols-2 gap-6 gap-y-4">
                                                <div class="col-span-2">
                                                    <text-field
                                                        label="Title"
                                                        v-model="accItem.question"
                                                    />
                                                </div>
                                                <div class="col-span-2">
                                                    <label class="flex justify-between items-center mb-1.5 text-sm">Description</label>
                                                    <text-editor
                                                        :text="accItem.answer"
                                                        @input="(newValue) => { accItem.answer = newValue }"
                                                    />
                                                </div>
                                            </div>
                                        </template>
                                    </draggable-accordion>
                                </template>
                            </draggable>
                            <btn type="secondary" size="small" icon-name="add-circle" @click="addAccordionItem(item)">Add item</btn>
                            <div class="flex justify-between items-center mt-4">
                                <toggle
                                    name="first_item_open"
                                    size="medium"
                                    @update:model-value="item.first_item_open = $event"
                                    :model-value="item.first_item_open ?? false"
                                />
                                <span class="flex flex-col flex-grow">
                                    <span class="text-sm font-medium">First item open by default</span>
                                </span>
                            </div>
                        </div>

                        <!-- Divider item -->
                        <div v-else-if="item.type === 'divider'" class="flex items-center gap-3 py-2 text-gray-400">
                            <hr class="flex-1 border-gray-300" />
                            <span class="text-xs uppercase tracking-wide">Divider</span>
                            <hr class="flex-1 border-gray-300" />
                        </div>

                        <!-- Links item -->
                        <div v-else-if="item.type === 'links'">
                            <div v-if="item.links.length" class="bg-white border rounded mb-2">
                                <draggable
                                    v-model="item.links"
                                    handle=".link-handle"
                                    item-key="unique_id"
                                    :animation="150"
                                    class="divide-y"
                                >
                                    <template #item="{ element: link, index }">
                                        <div class="p-3 space-y-2">
                                            <div class="flex items-center justify-between">
                                                <div class="flex items-center gap-2">
                                                    <button type="button" class="link-handle cursor-move focus:outline-none">
                                                        <svg class="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path></svg>
                                                    </button>
                                                    <span class="text-sm text-gray-900">Link {{ index + 1 }}</span>
                                                </div>
                                                <btn type="light" size="xs" @click="removeLink(item, link)">
                                                    <icon name="delete" size="14" class="mr-1" />
                                                    Delete
                                                </btn>
                                            </div>
                                            <div class="grid grid-cols-2 gap-2">
                                                <text-field v-model="link.title" placeholder="Title" />
                                                <text-field v-model="link.url" :prefixes="['https://', 'http://']" placeholder="https://" />
                                            </div>
                                            <text-field v-model="link.description" placeholder="Description (optional)" />
                                        </div>
                                    </template>
                                </draggable>
                            </div>
                            <btn type="secondary" size="small" @click="addLink(item)">Add link</btn>
                        </div>
                    </div>
                </div>
            </template>
        </draggable>

        <div class="w-max" :class="{ 'mt-4': props.data.items.length }">
            <options :is-top="true" :is-right="false">
                <template #toggle>
                    <btn type="secondary" size="small" icon-name="more" icon-right="true" v-on:click.prevent>Add item</btn>
                </template>
                <template v-slot:items="{ setShow }">
                    <options-item @click="addItem('text'); setShow(false)" title="Text"></options-item>
                    <options-item @click="addItem('image'); setShow(false)" title="Image"></options-item>
                    <options-item @click="addItem('contentBlock'); setShow(false)" title="Content section"></options-item>
                    <options-item @click="addItem('links'); setShow(false)" title="Links"></options-item>
                    <options-item @click="addItem('accordion'); setShow(false)" title="Accordion"></options-item>
                    <options-item @click="addItem('divider'); setShow(false)" title="Divider"></options-item>
                </template>
            </options>
        </div>
    </div>
</template>

<script setup>
import {defineProps} from 'vue';
import draggable from 'vuedraggable';
import {uniqueId} from '../helpers';
import ResourceFileUpload from "./ResourceFileUpload.vue";

const props = defineProps({
    data: Object,
    unique_id: String,
});

props.data.items = props.data?.items ?? [];

props.data.items.forEach(item => {
    if (!item.unique_id) item.unique_id = uniqueId();
    if (item.type === 'links' && item.links) {
        item.links.forEach(link => {
            if (!link.unique_id) link.unique_id = uniqueId();
        });
    }
    if (item.type === 'accordion' && item.items) {
        item.items.forEach(accItem => {
            if (!accItem.unique_id) accItem.unique_id = uniqueId();
        });
    }
});

const toggleItem = (item) => {
    const isOpening = !item.edit;
    if (isOpening) {
        props.data.items.forEach(i => { i.edit = false; });
    }
    item.edit = !item.edit;
};

const removeItem = (item) => {
    const index = props.data.items.indexOf(item);
    if (index !== -1) props.data.items.splice(index, 1);
};

const addItem = (type) => {
    props.data.items.forEach(item => { item.edit = false; });
    const base = { type, unique_id: uniqueId(), edit: true };

    switch (type) {
        case 'text':
            props.data.items.push({ ...base, content: '' });
            break;
        case 'image':
            props.data.items.push({ ...base, files: { content_block_image: {} }, image_border: true });
            break;
        case 'contentBlock':
            props.data.items.push({
                ...base,
                title: '',
                content: '',
                files: { content_block_image: {} },
                button: '',
                url: '',
                image_position: 'top',
                image_width: '1/2',
                image_border: true,
            });
            break;
        case 'links':
            props.data.items.push({ ...base, links: [] });
            break;
        case 'accordion':
            props.data.items.push({ ...base, items: [] });
            break;
        case 'divider':
            props.data.items.push({ ...base });
            break;
    }
};

const addLink = (item) => {
    item.links.push({ type: 'link', unique_id: uniqueId(), title: '', url: '', description: '' });
};

const removeLink = (item, link) => {
    item.links = item.links.filter((l) => l !== link);
};

const addAccordionItem = (item) => {
    item.items.forEach(i => { i.edit = false; });
    item.items.push({ unique_id: uniqueId(), edit: true, question: '', answer: '' });
};

const removeAccordionItem = (item, uid) => {
    const index = item.items.findIndex(i => i.unique_id === uid);
    if (index !== -1) item.items.splice(index, 1);
};

const toggleAccordionItem = (item, accItem) => {
    const isOpening = !accItem.edit;
    if (isOpening) {
        item.items.forEach(i => {
            if (i.unique_id !== accItem.unique_id) i.edit = false;
        });
    }
    accItem.edit = !accItem.edit;
};

const getItemLabel = (item) => {
    switch (item.type) {
        case 'text': return 'Text';
        case 'image': return 'Image';
        case 'contentBlock': return item.title || 'Content section';
        case 'links': return 'Links';
        case 'accordion': return 'Accordion';
        case 'divider': return 'Divider';
        default: return 'Item';
    }
};
</script>
